import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login, loginAsGuest, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);

  const redirect = sp.get("redirect") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "admin") nav("/admin");
      else nav(redirect);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const guest = async () => {
    setGuestBusy(true);
    try {
      await loginAsGuest();
      toast.success("Continuing as guest.");
      nav(redirect);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start guest session");
    } finally {
      setGuestBusy(false);
    }
  };

  return (
    <div className="fade-in max-w-md mx-auto px-6 py-24" data-testid="login-page">
      <div className="font-micro text-[#8A8680] mb-4">Studio Access</div>
      <h1 className="font-serif text-[#EDE4D3] text-5xl mb-10">Sign in.</h1>
      <form onSubmit={submit} className="space-y-6">
        <input className="input-teak" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
        <input className="input-teak" type="password" placeholder="Password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" />
        <button className="btn-bronze w-full" type="submit" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</button>
      </form>

      <div className="flex items-center gap-4 my-8" data-testid="login-divider">
        <div className="flex-1" style={{ borderTop: "1px solid var(--hairline)" }} />
        <div className="font-micro text-[#8A8680]">or</div>
        <div className="flex-1" style={{ borderTop: "1px solid var(--hairline)" }} />
      </div>

      <button className="btn-ghost w-full" onClick={guest} disabled={guestBusy} data-testid="login-guest">
        {guestBusy ? "Preparing…" : "Continue as Guest"}
      </button>
      <p className="font-micro text-[#8A8680] mt-3 text-center leading-relaxed">
        Guest checkout &mdash; no password, no account created for you to remember.
      </p>

      <div className="mt-10 text-[#8A8680] font-micro">
        New to the studio? <Link to="/register" className="text-[#B8895C] link-underline">Create account</Link>
      </div>
    </div>
  );
}
